import { buildDomainSeoBundle } from "./agenticSeoUtils";

export const MANUFACTURING_DOMAIN_INTRO = {
  title: "Manufacturing",
  content:
    "Agentic AI + Workflow Automation for Production Order Planning across automotive, cable, electronics, chemicals, plastics, packaging, textiles, industrial machinery, metal fabrication, food, pharma, and process manufacturing. Each use case pairs a real operational challenge with a governed automation solution — BOM checks, capacity validation, material availability, exception routing, and audit evidence — for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription:
    "Manufacturing agentic AI use cases — production order planning automation for 12 sub-sectors including automotive, cable, electronics, chemicals, plastics, packaging, textiles, industrial machinery, metal fabrication, food, pharma, and process manufacturing with BOM, capacity, and material availability checks.",
};

const slug = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function summaryFor(subIndustry) {
  return `${subIndustry} organizations can improve production order planning by using AI-enabled automation to capture inputs, validate rules, route exceptions, and update business systems. The solution is designed for practical implementation with human approval where needed. It helps business teams reduce manual effort, improve accuracy, and create a cleaner digital trail for operations, compliance, and management review.`;
}

function challengeFor(subIndustry) {
  return `Production Planning teams often manage production order planning for ${subIndustry} through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into machine utilization and delivery commitments.`;
}

const SOLUTION =
  "Deploy an Agentic AI + Workflow Automation solution to read orders, check BOM, capacity and material availability, then suggest a feasible production plan. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.";

function withMeta(uc) {
  return {
    ...uc,
    seoTitle: uc.title,
    title: uc.subIndustry,
    cardSubtitle: uc.department,
    groupId: slug(uc.subIndustry),
  };
}

const RAW = [
  {
    id: "production-order-planning-automotive-components",
    subIndustry: "Automotive Components",
    department: "Automation",
    businessProcess: "Production Order Planning for Automotive Components",
    regions: "India, UK, USA",
    title: "AI Production Order Planning Automation for Automotive Components",
    seoKeywords:
      "production order planning automation, automotive components AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Automotive Components"),
    solution: SOLUTION,
    summary: summaryFor("Automotive Components"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-cable-manufacturing",
    subIndustry: "Cable Manufacturing",
    department: "Automation",
    businessProcess: "Production Order Planning for Cable Manufacturing",
    regions: "India, UAE, KSA",
    title: "AI Production Order Planning Automation for Cable Manufacturing",
    seoKeywords:
      "production order planning automation, cable manufacturing AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Cable Manufacturing"),
    solution: SOLUTION,
    summary: summaryFor("Cable Manufacturing"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-electronics-manufacturing",
    subIndustry: "Electronics Manufacturing",
    department: "Automation",
    businessProcess: "Production Order Planning for Electronics Manufacturing",
    regions: "India, UAE, USA",
    title: "AI Production Order Planning Automation for Electronics Manufacturing",
    seoKeywords:
      "production order planning automation, electronics manufacturing AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Electronics Manufacturing"),
    solution: SOLUTION,
    summary: summaryFor("Electronics Manufacturing"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-chemicals",
    subIndustry: "Chemicals",
    department: "Automation",
    businessProcess: "Production Order Planning for Chemicals",
    regions: "India, UAE, KSA, UK, USA",
    title: "AI Production Order Planning Automation for Chemicals",
    seoKeywords:
      "production order planning automation, chemicals AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Chemicals"),
    solution: SOLUTION,
    summary: summaryFor("Chemicals"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-plastics",
    subIndustry: "Plastics",
    department: "Automation",
    businessProcess: "Production Order Planning for Plastics",
    regions: "India, UAE, KSA",
    title: "AI Production Order Planning Automation for Plastics",
    seoKeywords:
      "production order planning automation, plastics AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Plastics"),
    solution: SOLUTION,
    summary: summaryFor("Plastics"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-packaging",
    subIndustry: "Packaging",
    department: "Automation",
    businessProcess: "Production Order Planning for Packaging",
    regions: "India, UAE, KSA, UK",
    title: "AI Production Order Planning Automation for Packaging",
    seoKeywords:
      "production order planning automation, packaging AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Packaging"),
    solution: SOLUTION,
    summary: summaryFor("Packaging"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-textiles",
    subIndustry: "Textiles",
    department: "Automation",
    businessProcess: "Production Order Planning for Textiles",
    regions: "India, UAE, UK, USA",
    title: "AI Production Order Planning Automation for Textiles",
    seoKeywords:
      "production order planning automation, textiles AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Textiles"),
    solution: SOLUTION,
    summary: summaryFor("Textiles"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-industrial-machinery",
    subIndustry: "Industrial Machinery",
    department: "Automation",
    businessProcess: "Production Order Planning for Industrial Machinery",
    regions: "India, UAE, KSA, UK, USA",
    title: "AI Production Order Planning Automation for Industrial Machinery",
    seoKeywords:
      "production order planning automation, industrial machinery AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Industrial Machinery"),
    solution: SOLUTION,
    summary: summaryFor("Industrial Machinery"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-metal-fabrication",
    subIndustry: "Metal Fabrication",
    department: "Automation",
    businessProcess: "Production Order Planning for Metal Fabrication",
    regions: "India, UAE, KSA, UK, USA",
    title: "AI Production Order Planning Automation for Metal Fabrication",
    seoKeywords:
      "production order planning automation, metal fabrication AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Metal Fabrication"),
    solution: SOLUTION,
    summary: summaryFor("Metal Fabrication"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-food-manufacturing",
    subIndustry: "Food Manufacturing",
    department: "Automation",
    businessProcess: "Production Order Planning for Food Manufacturing",
    regions: "India, UAE, KSA, UK, USA",
    title: "AI Production Order Planning Automation for Food Manufacturing",
    seoKeywords:
      "production order planning automation, food manufacturing AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Food Manufacturing"),
    solution: SOLUTION,
    summary: summaryFor("Food Manufacturing"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-pharma-manufacturing",
    subIndustry: "Pharma Manufacturing",
    department: "Automation",
    businessProcess: "Production Order Planning for Pharma Manufacturing",
    regions: "India, UK, USA, KSA",
    title: "AI Production Order Planning Automation for Pharma Manufacturing",
    seoKeywords:
      "production order planning automation, pharma manufacturing AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Pharma Manufacturing"),
    solution: SOLUTION,
    summary: summaryFor("Pharma Manufacturing"),
    buyerPersona: "Plant Head, COO, Operations Head, CIO",
  },
  {
    id: "production-order-planning-process-manufacturing",
    subIndustry: "Process Manufacturing",
    department: "Automation",
    businessProcess: "Production Order Planning for Process Manufacturing",
    regions: "India, UAE, KSA, UK, USA",
    title: "AI Production Order Planning Automation for Process Manufacturing",
    seoKeywords:
      "production order planning automation, process manufacturing AI automation, manufacturing RPA services, agentic AI solution",
    challenge: challengeFor("Process Manufacturing"),
    solution: SOLUTION,
    summary: summaryFor("Process Manufacturing"),
    buyerPersona: "Plant Head, COO, Operations Head, Quality Head, CIO",
  },
];

export const MANUFACTURING_USE_CASES = RAW.map(withMeta);

const GROUP_ORDER = [
  "Automotive Components",
  "Cable Manufacturing",
  "Electronics Manufacturing",
  "Chemicals",
  "Plastics",
  "Packaging",
  "Textiles",
  "Industrial Machinery",
  "Metal Fabrication",
  "Food Manufacturing",
  "Pharma Manufacturing",
  "Process Manufacturing",
];

export const MANUFACTURING_GROUPS = GROUP_ORDER.map((label) => ({
  id: slug(label),
  label,
})).filter((g) => MANUFACTURING_USE_CASES.some((uc) => uc.groupId === g.id));

const manufacturingSeo = buildDomainSeoBundle({
  domainSlug: "manufacturing",
  domainIntro: MANUFACTURING_DOMAIN_INTRO,
  useCases: MANUFACTURING_USE_CASES,
});

export const MANUFACTURING_USE_CASES_SEO = manufacturingSeo.useCasesSeo;
export const MANUFACTURING_DOMAIN_FAQS = manufacturingSeo.domainFaqs;
export const MANUFACTURING_ITEM_LIST_JSONLD = manufacturingSeo.itemListJsonLd;
export const MANUFACTURING_FAQ_JSONLD = manufacturingSeo.faqJsonLd;
export const MANUFACTURING_BREADCRUMB_JSONLD = manufacturingSeo.breadcrumbJsonLd;
export const MANUFACTURING_WEBPAGE_JSONLD = manufacturingSeo.webPageJsonLd;

